import { Dialog } from '@base-ui/react/dialog';
import { MIO_CONTINUE_REQUEST, endsWithMioResponseLimit, isMioWithheldAnswer } from '@mio/shared';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  MessageSquare,
  PanelRightClose,
  PanelRightOpen,
  Settings2,
  Square,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState, type CSSProperties } from 'react';

import { cn } from '@/shared/lib/cn';
import { useResizableWidth } from '@/shared/lib/use-resizable-width';
import { ACTIVITY_KIND } from '@/shared/ui/activity/activity-kind';
import { ResizeHandle } from '@/shared/ui/resize-handle';
import { Skeleton } from '@/shared/ui/skeleton';
import { LoadingStatus } from '@/shared/ui/spinner';
import { UsageBalance } from '@/shared/usage/usage-balance';

import { executeMioAction } from '../actions.service';
import { useLiveContext } from '../use-live-context';
import { useMio } from '../use-mio';
import { usePageContext } from '../use-page-context';

import { MioCall } from './mio-call';
import { MioComposer } from './mio-composer';
import { MioMemoryPanel } from './mio-memory';
import { MioTranscript } from './mio-transcript';

import type { BillingUsage, MioTurnPhase } from '@mio/shared';

/** The original companion column, with persistent chat and voice sharing one conversation. */
const PHASE_LABELS: Record<MioTurnPhase, string> = {
  drafting: 'Mio is drafting an answer…',
  working: 'Mio is looking things up…',
  checking: 'Mio is checking the answer against the lesson and your files…',
  repairing: 'Mio is fixing the parts that did not check out…',
};

export function MioRail({ usage }: { usage?: BillingUsage | null | undefined }) {
  const mio = useMio();
  const page = usePageContext();
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState(false);
  const [mobile, setMobile] = useState(() => window.innerWidth < 1024);
  const portalContainer = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const preferencesButton = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const follow = useRef(true);
  const resizable = useResizableWidth({
    storageKey: 'mio.rail-width',
    defaultWidth: 368,
    min: 320,
    max: 880,
    reserve: page?.environment === 'studio' ? 1020 : 620,
  });
  useEffect(() => {
    const update = () => setMobile(window.innerWidth < 1024);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  useLiveContext(page, mio.tabId, mio.observeContext);
  const register = mio.actionHandler;
  useEffect(() => {
    register(async (action, signal) => {
      setPreferences(false);
      await executeMioAction(
        action,
        async (path) => {
          await navigate({ to: path });
        },
        signal,
      );
    });
    return () => register(null);
  }, [register, navigate]);
  useEffect(() => {
    if (follow.current)
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'instant' });
  }, [mio.messages, mio.pending]);

  const seeing = page?.activity
    ? `${ACTIVITY_KIND[page.activity.kind].label} · ${page.activity.title}`
    : page?.module
      ? `Module ${page.module.number} · ${page.module.title}`
      : 'Your learning workspace';
  const empty = mio.messages.length === 0;
  const activeVoice = mio.voiceStatus !== 'idle';
  const canSend = !mio.loading && mio.capabilities.chat && Boolean(page) && !usage?.ai.exhausted;
  // Only a finished reply that the API cut at its budget gets a one-tap continuation.
  const last = mio.messages.at(-1);
  const continuable =
    last?.role === 'mio' && last.status === 'complete' && endsWithMioResponseLimit(last.content);
  // A withheld answer is a platform notice; the same question usually verifies on a fresh draft.
  const retryQuestion =
    last?.role === 'mio' && last.status === 'complete' && isMioWithheldAnswer(last.content)
      ? mio.messages.findLast((message) => message.role === 'learner')?.content
      : undefined;
  const longConversation = mio.messages.length >= 24;

  return (
    <Dialog.Root
      open={mio.open}
      modal={mobile}
      disablePointerDismissal
      onOpenChange={(open, details) => {
        if (
          !mobile &&
          details.reason === 'escape-key' &&
          !panel.current?.contains(document.activeElement)
        ) {
          details.cancel();
          return;
        }
        mio.setOpen(open);
      }}
    >
      {activeVoice && !mio.open ? (
        <div
          className="border-mio-line bg-canvas text-fg fixed bottom-20 right-3 z-30 flex items-center gap-3 rounded-lg border p-3 text-xs shadow-sm lg:bottom-5 lg:right-16"
          aria-label="Active voice controls"
        >
          <button
            type="button"
            onClick={() => mio.setOpen(true)}
            className="inline-flex min-h-11 items-center gap-2 lg:min-h-8"
            aria-label="Open Mio voice controls"
          >
            <span className="bg-mio size-1.5 rounded-full" aria-hidden="true" />
            {mio.voiceStatus === 'paused'
              ? 'Voice paused'
              : mio.muted
                ? 'Microphone muted'
                : 'Voice active'}
          </button>
          <button
            type="button"
            onClick={mio.toggleMute}
            aria-pressed={mio.muted}
            className="text-fg-muted min-h-11 min-w-11 underline lg:min-h-8"
            aria-label={mio.muted ? 'Unmute microphone' : 'Mute microphone'}
          >
            {mio.muted ? 'Unmute' : 'Mute'}
          </button>
          <button
            type="button"
            onClick={mio.endCall}
            className="text-danger min-h-11 min-w-11 underline lg:min-h-8"
            aria-label="End voice conversation"
          >
            End
          </button>
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => mio.setOpen(true)}
        aria-label={activeVoice ? 'Open Mio · voice active' : 'Open Mio'}
        title="Open Mio (⌘J)"
        className={cn(
          'border-line group hidden h-full w-[var(--panel-rail-width)] shrink-0 flex-col items-center gap-1 rounded-lg border bg-[var(--panel-background)] pt-[var(--panel-padding)] transition-colors hover:bg-[var(--panel-selected)]',
          !mio.open && 'lg:flex',
        )}
      >
        <span className="relative inline-flex size-[var(--panel-row-height)] items-center justify-center">
          <MessageSquare
            className={cn(
              'text-fg-muted size-[18px] transition-opacity group-hover:animate-none group-hover:opacity-0 group-focus-visible:animate-none group-focus-visible:opacity-0',
              (mio.pending || activeVoice) && 'ui-loading-pulse animate-pulse',
            )}
            strokeWidth={1.7}
            aria-hidden="true"
          />
          <PanelRightOpen
            className="text-fg absolute size-[18px] opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
            strokeWidth={1.7}
            aria-hidden="true"
          />
        </span>
        <span className="text-fg text-[11px] font-medium leading-4">Mio</span>
        {activeVoice ? (
          <span className="text-fg-muted max-w-10 text-center text-[10px] leading-3">
            Voice active
          </span>
        ) : null}
      </button>
      <div ref={portalContainer} className="contents" />
      <Dialog.Portal container={portalContainer} keepMounted className="contents">
        <Dialog.Popup
          ref={panel}
          render={<aside />}
          role={mobile ? 'dialog' : 'complementary'}
          aria-modal={mobile || undefined}
          initialFocus={mobile ? closeButton : false}
          finalFocus={mobile}
          className={cn(
            'mio-panel bg-canvas flex flex-col outline-none [&_button]:min-h-11 lg:[&_button]:min-h-8',
            'fixed inset-0 z-40',
            'lg:border-line lg:relative lg:inset-auto lg:z-auto lg:h-full lg:w-[var(--mio-width)] lg:shrink-0 lg:rounded-lg lg:border',
            resizable.dragging && 'lg:border-line-strong',
            !mio.open && 'hidden',
          )}
          style={{ '--mio-width': `${resizable.width}px` } as CSSProperties}
          aria-label="Mio"
        >
          {!mobile ? (
            <ResizeHandle resizable={resizable} label="Resize Mio" className="mio-panel-resize" />
          ) : null}
          <header className="border-line flex min-h-[var(--panel-header-height)] shrink-0 items-center gap-2 border-b bg-[var(--panel-background)] px-[var(--panel-padding)] py-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Dialog.Title className="text-fg text-sm font-semibold">Mio</Dialog.Title>
                <span className="text-fg-muted truncate text-xs">Learning assistant</span>
              </div>
              <p className="text-fg-muted mt-0.5 truncate text-xs leading-4" title={seeing}>
                {seeing}
              </p>
            </div>
            <button
              ref={preferencesButton}
              type="button"
              onClick={() => setPreferences((value) => !value)}
              className="text-fg-muted hover:text-fg hover:bg-surface inline-flex size-11 shrink-0 items-center justify-center rounded-[var(--panel-radius)] lg:size-8"
              aria-label="Memory and preferences"
              aria-pressed={preferences}
              title="Memory and preferences"
            >
              <Settings2 className="size-4" strokeWidth={1.7} aria-hidden="true" />
            </button>
            <Dialog.Close
              ref={closeButton}
              className="text-fg-subtle hover:text-fg hover:bg-surface inline-flex size-11 shrink-0 items-center justify-center rounded-[var(--panel-radius)] lg:size-8"
              aria-label={mobile ? 'Close Mio' : 'Collapse Mio'}
              title={mobile ? 'Close Mio' : 'Collapse Mio (⌘J)'}
            >
              {mobile ? (
                <X className="size-5" strokeWidth={1.7} aria-hidden="true" />
              ) : (
                <PanelRightClose className="size-4" strokeWidth={1.7} aria-hidden="true" />
              )}
            </Dialog.Close>
          </header>

          <UsageBalance
            usage={usage}
            compact
            className="border-line shrink-0 border-b px-4 py-2"
            onManage={() => {
              if (mobile) mio.setOpen(false);
            }}
          />

          {activeVoice ? (
            <MioCall
              status={mio.voiceStatus}
              caption={mio.voiceCaption}
              muted={mio.muted}
              pending={mio.pending}
              diagnostics={mio.voiceDiagnostics}
              microphoneId={mio.microphoneId}
              onSelectMicrophone={mio.selectMicrophone}
              onMute={mio.toggleMute}
              onEnd={mio.endCall}
              onResume={() => void mio.startCall()}
            />
          ) : null}

          {preferences ? (
            <MioMemoryPanel
              memory={mio.memory}
              profile={mio.profile}
              voices={mio.voices}
              onSaveProfile={mio.saveProfile}
              onSaveMemory={mio.saveMemory}
              onRemoveMemory={mio.removeMemory}
              onResetMemory={mio.resetMemory}
              onLoadVoices={mio.loadVoices}
              onClose={() => {
                setPreferences(false);
                preferencesButton.current?.focus();
              }}
            />
          ) : null}
          <div
            hidden={preferences}
            className={preferences ? 'hidden' : 'flex min-h-0 flex-1 flex-col'}
          >
            <div
              ref={scrollRef}
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-6 pt-4"
              onScroll={(event) => {
                const element = event.currentTarget;
                follow.current =
                  element.scrollHeight - element.scrollTop - element.clientHeight < 100;
              }}
            >
              {mio.loading ? (
                <div className="space-y-4">
                  <LoadingStatus label="Loading your conversation…" className="text-xs" />
                  <Skeleton className="h-16 w-5/6" />
                  <Skeleton className="ml-auto h-12 w-3/4" />
                </div>
              ) : null}
              {empty && !mio.loading ? (
                <div className="pt-1">
                  <p className="text-fg text-[15px] leading-6">
                    Ask a question, work through an example, or get another perspective on the
                    lesson.
                  </p>
                  <p className="text-fg-subtle mt-3 text-xs leading-5">
                    {page?.environment === 'workshop'
                      ? 'Mio can help with this workshop: ask about the brief, your code, a failing check or an error, and Mio will guide you toward the solution without giving it away.'
                      : 'Mio follows the section you’re viewing. Your editor and files stay outside this context.'}
                  </p>
                </div>
              ) : null}
              {!mio.loading && !mio.capabilities.chat ? (
                <p className="text-fg-muted mt-4 text-xs leading-5" role="status">
                  Mio is not available from the server yet. Your lessons remain available.
                </p>
              ) : null}
              {empty && canSend ? (
                <div className="border-line mt-5 flex flex-col border-t">
                  {mio.suggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => {
                        follow.current = true;
                        void mio.send(suggestion, page ?? undefined);
                      }}
                      className="border-line text-fg hover:bg-surface flex w-full items-center justify-between gap-3 border-b py-3 text-left text-sm leading-5 transition-colors"
                    >
                      <span className="min-w-0 [overflow-wrap:anywhere]">{suggestion}</span>
                      <ArrowRight className="size-4 shrink-0" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              ) : null}
              <div className={empty ? '' : 'pt-1'}>
                <MioTranscript messages={mio.messages} />
              </div>
              {continuable && canSend && !mio.pending ? (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => {
                      follow.current = true;
                      void mio.send(MIO_CONTINUE_REQUEST, page ?? undefined);
                    }}
                    className="border-line text-fg hover:bg-surface inline-flex min-h-11 items-center gap-1.5 rounded border px-3 text-xs transition-colors lg:min-h-8"
                    aria-label="Continue Mio's answer"
                  >
                    Continue
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </button>
                </div>
              ) : null}
              {retryQuestion && canSend && !mio.pending ? (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => {
                      follow.current = true;
                      void mio.send(retryQuestion, page ?? undefined);
                    }}
                    className="border-line text-fg hover:bg-surface inline-flex min-h-11 items-center gap-1.5 rounded border px-3 text-xs transition-colors lg:min-h-8"
                    aria-label="Ask Mio the same question again"
                  >
                    Try again
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </button>
                </div>
              ) : null}
              {mio.pending ? (
                <div className="text-fg-subtle mt-5 flex items-center justify-between gap-2 text-xs">
                  <LoadingStatus
                    label={mio.phase ? PHASE_LABELS[mio.phase] : 'Mio is working…'}
                    className="text-xs"
                  />
                  <button
                    type="button"
                    onClick={mio.cancel}
                    className="text-fg-muted hover:text-fg inline-flex min-h-11 items-center gap-1.5 p-2 lg:min-h-8"
                    aria-label="Stop response"
                  >
                    <Square size={12} aria-hidden="true" />
                    Stop
                  </button>
                </div>
              ) : null}
              {mio.error ? (
                <div className="mt-4">
                  <p role="alert" className="text-danger text-xs leading-5">
                    {mio.error}
                  </p>
                  <button
                    type="button"
                    onClick={mio.retry}
                    className="text-fg-muted mt-2 min-h-11 text-xs underline lg:min-h-8"
                  >
                    Reconnect Mio
                  </button>
                </div>
              ) : null}
              {!mio.connected && !mio.loading && mio.capabilities.chat ? (
                <LoadingStatus label="Reconnecting live updates…" className="mt-3 text-xs" />
              ) : null}
            </div>
            <div className="px-4 pb-4">
              <MioComposer
                disabled={!canSend}
                onSend={(text) => {
                  follow.current = true;
                  void mio.send(text, page ?? undefined);
                }}
                {...(!activeVoice &&
                mio.capabilities.voice &&
                !usage?.voice.exhausted &&
                !usage?.ai.exhausted
                  ? { onCall: () => void mio.startCall() }
                  : {})}
              />
              <div className="text-fg-subtle mt-2 flex items-center justify-between gap-2 px-1 text-xs">
                <span>
                  {longConversation
                    ? 'Long conversation. A new one keeps answers fast.'
                    : 'Private to you'}
                </span>
                {!empty ? (
                  <button
                    type="button"
                    onClick={() => void mio.clear()}
                    disabled={mio.loading}
                    className="hover:text-fg min-h-11 lg:min-h-8"
                  >
                    New conversation
                  </button>
                ) : (
                  <span>⌘J to hide</span>
                )}
              </div>
            </div>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
